import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * LR HUB quick-profile popup data for ANY member.
 *
 * Reads the `hub_public_profile` security-definer RPC, which returns only
 * display-safe Hub fields (display name, @handle, role ONLY when the member
 * kept "show my role" on, APPROVED avatar path, admin-assigned badge,
 * hub_settings.created_at) plus approved/non-deleted `hub_posts` and
 * `hub_post_comments` counts.
 *
 * ISOLATION: Hub tables only. No billing / company / verification reads.
 * READ-ONLY: no mutations anywhere in this path.
 */

export interface HubPublicProfile {
  user_id: string;
  display_name: string;
  handle: string;
  role: string | null;
  avatar_path: string | null;
  hub_badge: string | null;
  member_since: string | null;
  posts_count: number;
  comments_count: number;
}

export function useHubPublicProfile(userId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ["hub-public-profile", userId ?? "none"],
    enabled: !!userId && enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<HubPublicProfile | null> => {
      const { data, error } = await supabase.rpc("hub_public_profile", { _user_id: userId! });
      if (error) throw error;
      const rows = (data ?? []) as HubPublicProfile[];
      return rows[0] ?? null;
    },
  });
}

export default useHubPublicProfile;
